import {
  Box,
  Grid,
  IconButton,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { generatePath, Link } from "react-router-dom";
import XIcon from "@mui/icons-material/X";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import NgwaahiaIcon from "../../../common-components/NgwaahiaIcon";
import {
  frontendRoutes,
  LANDING_STAY_QUERY_PARAM,
} from "../../../utility/frontendRoutes";
import { landingColors } from "../landingColors";
import { useFeatureFlag } from "../../../hooks/useFeatureFlags";
import useLandingSectionLink from "../hooks/useLandingSectionLink";
import SectionContainer from "./SectionContainer";

// A logged-in user can land here via AppFooter's "?stay=true" link — this
// footer's own logo needs the same bypass, or clicking it while already on
// the landing page (a fresh "/" navigation with no stay param) just
// re-triggers LandingPage's redirect-to-feed guard.
const LANDING_LINK = `${frontendRoutes.LANDING}?${LANDING_STAY_QUERY_PARAM}=true`;

// Sized a little under the 20px MUI uses for fontSize="small" because the
// storefront glyph fills its viewBox more fully than the social marks do, so
// matching pixel sizes would make it read as the largest icon in the row.
// currentColor lets it inherit the IconButton's color and hover state.
const NgwaahiaShopIcon = () => <NgwaahiaIcon size={18} color="currentColor" />;

// Facebook and LinkedIn handles not yet confirmed — those two stay placeholders
// until supplied. The shop link is in-app (ShopPage resolves the "@" prefix as a
// handle), so it routes through react-router rather than a full page load.
const SOCIAL_LINKS = [
  // { label: "X", icon: XIcon, href: "#" },
  // { label: "Facebook", icon: FacebookIcon, href: "#" },
  // { label: "LinkedIn", icon: LinkedInIcon, href: "#" },
  {
    label: "Instagram",
    icon: InstagramIcon,
    href: "https://www.instagram.com/ngwa_ahia",
  },
  {
    label: "Ngwaahia Shop",
    icon: NgwaahiaShopIcon,
    to: generatePath(frontendRoutes.VIEW_SHOP, { shopId: "@ngwaahia" }),
  },
];

const QUICK_LINKS = [
  { label: "Features", sectionId: "features" },
  { label: "FAQ", sectionId: "faq" },
  { label: "Browse Marketplace", to: frontendRoutes.HOME },
];

// The home for long-form content. "What Does Ngwaahia Mean?" moved here out of
// Quick Links, and this is where a blog would slot in alongside the guides.
const LEARN_LINKS = [
  { label: "How Ngwaahia Works: A Story", to: frontendRoutes.STORY_HOW_TO_USE },
  { label: "Help Center", to: frontendRoutes.HELP },
  { label: "What Does Ngwaahia Mean?", to: frontendRoutes.WHAT_IS_NGWAAHIA },
];

// Appended to LEARN_LINKS only when the "roll-credits" flag is on. The page
// itself stays reachable either way, so contributors can be sent a link to
// their own credit to confirm before the roll is announced.
const CREDITS_LINK = { label: "Roll Credits!", to: frontendRoutes.CREDITS };

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: frontendRoutes.PRIVACY_POLICY },
  { label: "Terms of Service", to: frontendRoutes.TERMS_OF_SERVICE },
];

const linkSx = {
  color: landingColors.textSecondary,
  textDecoration: "none",
  fontSize: 14,
  "&:hover": { color: landingColors.primary },
};

const LandingFooter = () => {
  const sectionLink = useLandingSectionLink();
  const showCredits = useFeatureFlag("roll-credits");

  const learnLinks = showCredits ? [...LEARN_LINKS, CREDITS_LINK] : LEARN_LINKS;

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: landingColors.background,
        borderTop: `1px solid ${landingColors.border}`,
      }}
    >
      <SectionContainer sx={{ py: { xs: 5, md: 6 } }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box
              component={Link}
              to={LANDING_LINK}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
                textDecoration: "none",
              }}
            >
              <NgwaahiaIcon size={28} color={landingColors.primary} />
              <Typography fontWeight={700} color={landingColors.primary}>
                Ngwaahia
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: landingColors.textSecondary, mb: 2 }}
            >
              Connect. Trade. Thrive.
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {SOCIAL_LINKS.map(({ label, icon: Icon, href, to }) => {
                // Placeholder "#" entries stay in-tab: opening a second copy of
                // the current page is worse than the no-op. react-router's Link
                // hands off to the browser when target is set, so the shop link
                // opens in a new tab too rather than client-side navigating.
                const opensNewTab = Boolean(to) || href !== "#";

                return (
                  <IconButton
                    key={label}
                    {...(to
                      ? { component: Link, to }
                      : { component: "a", href })}
                    {...(opensNewTab
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    aria-label={label}
                    size="small"
                    sx={{ color: landingColors.textSecondary }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                );
              })}
            </Box>
          </Grid>

          {/* Nested so Quick Links / Learn / Legal / Need Help divide this
            8/12 slot into exact quarters (md=3 each) — four flat md items here
            would need repeating decimals (8/4 of 12) that round past 12 and
            wrap the last column instead of sitting inline. */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={4}>
              {[
                { heading: "Quick Links", links: QUICK_LINKS },
                { heading: "Learn", links: learnLinks },
                { heading: "Legal", links: LEGAL_LINKS },
              ].map(({ heading, links }) => (
                <Grid item xs={6} md={3} key={heading}>
                  <Typography
                    variant="overline"
                    sx={{ color: landingColors.disabled, fontWeight: 700 }}
                  >
                    {heading}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.25,
                      mt: 1,
                    }}
                  >
                    {links.map((link) => (
                      <MuiLink
                        key={link.label}
                        {...(link.sectionId
                          ? sectionLink(link.sectionId)
                          : { component: Link, to: link.to })}
                        sx={linkSx}
                      >
                        {link.label}
                      </MuiLink>
                    ))}
                  </Box>
                </Grid>
              ))}

              <Grid item xs={6} md={3}>
                <Typography
                  variant="overline"
                  sx={{ color: landingColors.disabled, fontWeight: 700 }}
                >
                  Need Help?
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <MuiLink
                    component={Link}
                    to={frontendRoutes.SUPPORT}
                    sx={linkSx}
                  >
                    Support
                  </MuiLink>
                  <Typography
                    variant="body2"
                    sx={{ color: landingColors.textSecondary, mt: 1 }}
                  >
                    <MuiLink href="mailto:support@ngwaahia.com" sx={linkSx}>
                      {/* Half a narrow phone's width is less than the full
                        address, so let it wrap at the @ when it has to. */}
                      support@
                      <wbr />
                      ngwaahia.com
                    </MuiLink>
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 5, color: landingColors.disabled }}
        >
          &copy; {new Date().getFullYear()} Ngwaahia. All rights reserved.
        </Typography>
      </SectionContainer>
    </Box>
  );
};

export default LandingFooter;
